import React, { useState } from "react";
import {
  Routes,
  Route,
  useLocation,
  Navigate,
  NavLink,
  useNavigate,
} from "react-router-dom";
import { ChakraProvider } from "@chakra-ui/react";
import "./App.css";
import logo from "./dlp-logo-text.png";
import Settings from "./components/Settings";
import Login from "./components/Login";
import DataExchange from "./components/DataExchange";
import AdminDashboard from "./components/admin/AdminDashboard";
import ConsultantDashboard from "./components/ConsultantDashboard";
import CreateClient from "./components/admin/CreateClient";
import EditClient from "./components/admin/EditClient";
import Sidebar from "./components/Sidebar";
import ProtectedRoute from "./components/ProtectedRoute";
import { UserProvider, useUser } from "./contexts/UserContext";
import { MsalProvider } from "@azure/msal-react";
import { CustomNavigationClient } from "./utils/NavigationClient";
import CreateConsultant from './components/admin/CreateConsultant';
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/admin/ResetPassword";
import ChangePassword from "./components/ChangePassword";

/**
 * @typedef {Object} User
 * @property {number} id
 * @property {string} name
 * @property {string} email
 * @property {string} role
 * @property {string} [clientId]
 * @property {string} [clientName]
 * @property {string} [databaseName]
 */

// Sidebar navigation items
const getNavigationItems = (user) => {
  const items = [];

  if (user.role === "admin") {
    items.push({
      to: "/admin",
      icon: "fa-cogs",
      text: "Administration",
    });
  }

  if (user.role === "admin" || user.role === "consultant") {
    items.push({
      to: "/clients",
      icon: "fa-users",
      text: "Konsulent",
    });
  }

  // Tilføj kunde-specifikke menupunkter hvis en kunde er valgt
  if (user.clientId) {
    items.push(
      {
        to: `/client/${user.clientId}/dataexchange`,
        icon: "fa-exchange-alt",
        text: "Dataudveksling",
      },
      {
        to: `/client/${user.clientId}/settings`,
        icon: "fa-cog",
        text: "Indstillinger",
      }
    );
  }

  return items;
};

function AppContent() {
  const { user, isLoggedIn, logout } = useUser();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const navigationItems = getNavigationItems(user || {});

  // Hvis brugeren er på login-siden eller forgot-password siden
  if (location.pathname === "/login" || location.pathname === "/forgot-password") {
    if (isLoggedIn) {
      return (
        <Navigate
          to={
            user.role === "admin"
              ? "/admin"
              : `/client/${user.clientId}/dataexchange`
          }
          replace
        />
      );
    }
    return location.pathname === "/login" ? <Login /> : <ForgotPassword />;
  }

  // Tving brugeren til at ændre adgangskode ved første login
  if (isLoggedIn && user?.firstLogin && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }

  // Redirect til login hvis brugeren ikke er logget ind
  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }

  // Hovedapplikationen
  return (
    <div className="App">
      {isLoggedIn && (
        <>
          <Sidebar isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen}>
            {user.clientId && user.clientName && (
              <div className="active-client">
                <i className="fas fa-building"></i>
                <span>{user.clientName}</span>
              </div>
            )}
            <nav>
              {navigationItems.map((item, index) => (
                <NavLink
                  key={index}
                  to={item.to}
                  className={({ isActive }) =>
                    `nav-link ${isActive ? "active" : ""}`
                  }
                >
                  <i className={`fas ${item.icon}`}></i>
                  <span>{item.text}</span>
                </NavLink>
              ))}
            </nav>
          </Sidebar>
          <div className="main-content">
            <header>
              <div className="logo">
                <img src={logo} alt="DLP Logo" className="App-logo" />
              </div>
              <div className="user-info">
                <span>{user?.name}</span>
                <button onClick={logout} className="logout-button">
                  <i className="fas fa-sign-out-alt"></i>
                  Log ud
                </button>
              </div>
            </header>
            <main>
              <Routes>
                {/* Root redirect */}
                <Route
                  path="/"
                  element={
                    <Navigate
                      to={
                        user.role === "admin"
                          ? "/admin"
                          : user.role === "consultant"
                          ? "/clients"
                          : `/client/${user.clientId}/dataexchange`
                      }
                      replace
                    />
                  }
                />

                {/* Admin routes */}
                <Route path="/admin" element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AdminDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/admin/create-client" element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <CreateClient />
                  </ProtectedRoute>
                } />
                <Route path="/admin/edit-client/:clientId" element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <EditClient />
                  </ProtectedRoute>
                } />
                <Route path="/admin/create-consultant" element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <CreateConsultant />
                  </ProtectedRoute>
                } />
                <Route path="/admin/reset-password" element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <ResetPassword />
                  </ProtectedRoute>
                } />

                {/* Consultant routes */}
                <Route
                  path="/clients"
                  element={
                    <ProtectedRoute allowedRoles={["admin", "consultant"]}>
                      <ConsultantDashboard />
                    </ProtectedRoute>
                  }
                />

                {/* Client routes */}
                <Route
                  path="/client/:clientId/dataexchange"
                  element={
                    <ProtectedRoute
                      requireClientId={true}
                      customCheck={(user) => {
                        const clientId = location.pathname.split("/")[2];
                        return (
                          user.role === "admin" ||
                          user.role === "consultant" ||
                          (user.role === "client" && user.clientId === clientId)
                        );
                      }}
                    >
                      <DataExchange />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/client/:clientId/settings"
                  element={
                    <ProtectedRoute
                      requireClientId={true}
                      customCheck={(user) => {
                        const clientId = location.pathname.split("/")[2];
                        return (
                          user.role === "admin" ||
                          user.role === "consultant" ||
                          (user.role === "client" && user.clientId === clientId)
                        );
                      }}
                    >
                      <Settings />
                    </ProtectedRoute>
                  }
                />

                {/* Change password route til alle brugerroller */}
                <Route path="/change-password" element={
                  <ProtectedRoute>
                    <ChangePassword />
                  </ProtectedRoute>
                } />
              </Routes>
            </main>
            <footer>© 2025 Din Lønpartner ApS. All Rights Reserved</footer>
          </div>
        </>
      )}
    </div>
  );
}

function App({ pca }) {
  const navigate = useNavigate();
  const navigationClient = new CustomNavigationClient(navigate);
  pca.setNavigationClient(navigationClient);
  return (
    <MsalProvider instance={pca}>
      <ChakraProvider>
        <UserProvider>
          <AppContent />
        </UserProvider>
      </ChakraProvider>
    </MsalProvider>
  );
}

export default App;
